import { useState, useEffect, useMemo } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import {
  Award,
  DollarSign,
  FileText,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import { useApi } from '../../../shared/hooks/useApi'

interface Contractor {
  contractor_uuid: string
  contractor_name: string
  total_value: number
  contract_count: number
  avg_value: number
  rank: number
}

interface ContractorDashboardProps {
  year?: number
  cpvCode?: string
  contractorName?: string
  onContractorClick: (contractorUuid: string) => void
  onRefresh: () => void
}

interface ChartDataItem {
  name: string
  fullName: string
  wartość: number
  wygrane: number
}

type SortKey = keyof Contractor

const ContractorDashboard = ({
  year,
  cpvCode,
  contractorName,
  onContractorClick,
  onRefresh,
}: ContractorDashboardProps) => {
  const { getTopContractors } = useApi()
  const [data, setData] = useState<Contractor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({
    key: 'rank',
    direction: 'asc',
  })

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        const results = await getTopContractors(year, 20, cpvCode, contractorName)
        setData(results)
      } catch (err) {
        console.error('Failed to load contractor stats:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [year, cpvCode, contractorName, getTopContractors])

  const sortedData = useMemo(() => {
    if (!data) return []

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key]
      const bValue = b[sortConfig.key]

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1
      }
      return 0
    })
  }, [data, sortConfig])

  const requestSort = (key: SortKey) => {
    let direction: 'asc' | 'desc' = 'asc'
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key, direction })
  }

  const SortIcon = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="h-4 w-4 ml-1 text-ink-faint" />
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-4 w-4 ml-1 text-brand" />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1 text-brand" />
    )
  }

  const SortableHeader = ({
    label,
    columnKey,
    align = 'left',
  }: {
    label: string
    columnKey: SortKey
    align?: 'left' | 'right'
  }) => (
    <th
      scope="col"
      className={`px-6 py-3 text-${align} text-xs font-medium text-ink-muted uppercase tracking-wider cursor-pointer hover:bg-surface-hover transition-colors`}
      onClick={() => requestSort(columnKey)}
    >
      <div className={`flex items-center ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        <SortIcon columnKey={columnKey} />
      </div>
    </th>
  )

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    )
  }

  if (error) {
    return <div className="text-danger p-4">Błąd: {error}</div>
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-ink-muted p-8 text-center">
        <p className="mb-4">Brak danych dla wybranych kryteriów.</p>
        <button
          onClick={onRefresh}
          className="inline-flex items-center px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-hover transition-colors"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Przelicz statystyki
        </button>
      </div>
    )
  }

  const chartData: ChartDataItem[] = data.slice(0, 10).map((item) => ({
    name:
      item.contractor_name.length > 20
        ? item.contractor_name.substring(0, 20) + '...'
        : item.contractor_name,
    fullName: item.contractor_name,
    wartość: item.total_value,
    wygrane: item.contract_count,
  }))

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Value Chart */}
        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <h3 className="text-lg font-medium text-ink mb-4 flex items-center">
            <DollarSign className="h-5 w-5 mr-2" />
            Top 10 Wykonawców wg Wartości
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis type="number" tickFormatter={(val) => `${(val / 1000000).toFixed(0)}M`} tick={{ fill: 'var(--color-ink-muted)' }} />
              <YAxis dataKey="name" type="category" width={150} tick={{ fill: 'var(--color-ink-muted)' }} />
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--color-surface-raised)', border: '1px solid var(--color-border)', color: 'var(--color-ink)' }}
                formatter={(value) =>
                  new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(
                    value as number,
                  )
                }
                labelFormatter={(label) =>
                  chartData.find((d) => d.name === label)?.fullName || label
                }
              />
              <Bar dataKey="wartość" fill="var(--color-brand)" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Count Chart */}
        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <h3 className="text-lg font-medium text-ink mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Top 10 Wykonawców wg Liczby Wygranych
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={chartData.sort((a, b) => b.wygrane - a.wygrane)}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis type="number" tick={{ fill: 'var(--color-ink-muted)' }} />
              <YAxis dataKey="name" type="category" width={150} tick={{ fill: 'var(--color-ink-muted)' }} />
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--color-surface-raised)', border: '1px solid var(--color-border)', color: 'var(--color-ink)' }}
                labelFormatter={(label) =>
                  chartData.find((d) => d.name === label)?.fullName || label
                }
              />
              <Bar dataKey="wygrane" fill="var(--color-success)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-surface-raised rounded-lg border border-border-subtle overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center">
          <Award className="h-5 w-5 mr-2 text-ink-muted" />
          <h3 className="text-lg font-medium text-ink">Ranking Wykonawców</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface">
              <tr>
                <SortableHeader label="#" columnKey="rank" />
                <SortableHeader label="Wykonawca" columnKey="contractor_name" />
                <SortableHeader label="Wartość całkowita" columnKey="total_value" align="right" />
                <SortableHeader
                  label="Wygrane postępowania"
                  columnKey="contract_count"
                  align="right"
                />
                <SortableHeader label="Średnia wartość" columnKey="avg_value" align="right" />
              </tr>
            </thead>
            <tbody className="bg-surface-raised divide-y divide-border">
              {sortedData.map((item) => (
                <tr key={item.contractor_uuid} className="hover:bg-surface-hover">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
                    #{item.rank}
                  </td>
                  <td
                    className="px-6 py-4 whitespace-nowrap text-sm font-medium text-brand cursor-pointer hover:underline"
                    onClick={() => onContractorClick && onContractorClick(item.contractor_uuid)}
                    title={item.contractor_name}
                  >
                    {item.contractor_name.length > 80
                      ? `${item.contractor_name.substring(0, 80)}...`
                      : item.contractor_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink text-right">
                    {new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(
                      item.total_value,
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink text-right">
                    {item.contract_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted text-right">
                    {new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(
                      item.avg_value,
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default ContractorDashboard
